/**
 * OnboardingGuide — 5-slide carousel introducing Lawhelper features
 * Harvey AI / Notion inspired institutional minimalist design
 */

import React, { useState, useCallback } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import './OnboardingGuide.css';

const STORAGE_KEY = 'lawhelper-onboarding-completed';

interface OnboardingGuideProps {
  currentLanguage: string;
  onClose: () => void;
}

interface Slide {
  titleKey: string;
  descKey: string;
  image: string;
}

const slides: Slide[] = [
  {
    titleKey: 'onboardingSlide1Title',
    descKey: 'onboardingSlide1Desc',
    image: '/onboarding/ai-chat.png',
  },
  {
    titleKey: 'onboardingSlide2Title',
    descKey: 'onboardingSlide2Desc',
    image: '/onboarding/case-system.png',
  },
  {
    titleKey: 'onboardingSlide3Title',
    descKey: 'onboardingSlide3Desc',
    image: '/onboarding/local-folders.png',
  },
  {
    titleKey: 'onboardingSlide4Title',
    descKey: 'onboardingSlide4Desc',
    image: '/onboarding/draft-review.png',
  },
];

const OnboardingGuide: React.FC<OnboardingGuideProps> = ({ currentLanguage, onClose }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const { t } = useTranslation(currentLanguage);

  const handleComplete = useCallback(() => {
    localStorage.setItem(STORAGE_KEY, 'true');
    onClose();
  }, [onClose]);

  const handleNext = useCallback(() => {
    if (currentSlide < slides.length - 1) {
      setCurrentSlide(prev => prev + 1);
    } else {
      handleComplete();
    }
  }, [currentSlide, handleComplete]);

  const handlePrev = useCallback(() => {
    if (currentSlide > 0) {
      setCurrentSlide(prev => prev - 1);
    }
  }, [currentSlide]);

  const handleOverlayClick = useCallback((e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleComplete();
    }
  }, [handleComplete]);

  const slide = slides[currentSlide];
  const isLast = currentSlide === slides.length - 1;

  return (
    <div className="onboarding-overlay" onClick={handleOverlayClick}>
      <div className="onboarding-card">
        {/* Image */}
        <div className="onboarding-image-wrapper">
          <img
            src={slide.image}
            alt={t(slide.titleKey as any)}
            draggable={false}
          />
          <div className="onboarding-step-badge">
            {currentSlide + 1} / {slides.length}
          </div>
        </div>

        {/* Content */}
        <div className="onboarding-content">
          <h2 className="onboarding-title">{t(slide.titleKey as any)}</h2>
          <p className="onboarding-desc">{t(slide.descKey as any)}</p>

          {/* Dots */}
          <div className="onboarding-dots">
            {slides.map((_, idx) => (
              <button
                key={idx}
                className={`onboarding-dot${idx === currentSlide ? ' active' : ''}`}
                onClick={() => setCurrentSlide(idx)}
                aria-label={`Slide ${idx + 1}`}
              />
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="onboarding-footer">
          <button className="onboarding-btn-skip" onClick={handleComplete}>
            {t('onboardingSkip')}
          </button>
          <div className="onboarding-btn-group">
            {currentSlide > 0 && (
              <button className="onboarding-btn-prev" onClick={handlePrev}>
                {t('onboardingPrev')}
              </button>
            )}
            <button className="onboarding-btn-next" onClick={handleNext}>
              {isLast ? t('onboardingGetStarted') : t('onboardingNext')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingGuide;

/** Utility: check if onboarding has been completed */
export const isOnboardingCompleted = (): boolean => {
  return localStorage.getItem(STORAGE_KEY) === 'true';
};

/** Utility: reset onboarding state (for re-showing from sidebar) */
export const resetOnboarding = (): void => {
  localStorage.removeItem(STORAGE_KEY);
};
